import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useTranslation } from "react-i18next";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { DatePickerField, InputField } from "@/features/FormCard/Fields";
import { Tables } from "@/gen/tableNames/tables";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import {
  dateSchema,
  parseIntValue,
  parseToDateValue,
} from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { Clients } from "@/lib/clients";

const FormSchema = z.object({
  strategy_id: z.coerce.number(),
  portfolio_id: z.coerce.number().optional().nullable(),
  credit_quality_step: z.preprocess(parseIntValue, z.number().int().nullable()),
  date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  book_value: z.coerce.number().optional().nullable(),
});

const formSchemaFields = FormSchema.keyof().enum;

const client = Clients.E2;

export function E2DynamicDatasNew() {
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name: Tables.E2_DYNAMIC_DATA,
    FormSchema,
    isNew: true,
  });

  const { t } = useTranslation("tables");

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full ">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${Tables.E2_DYNAMIC_DATA}.new_position`)}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_id}
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.strategy_id`)}
                    control={form.control}
                    isRequired
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.portfolio_id}
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.portfolio_id`)}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchemaFields.date}
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.date`)}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.credit_quality_step}
                    label={t(
                      `${Tables.E2_DYNAMIC_DATA}.columns.credit_quality_step`,
                    )}
                    control={form.control}
                    allowDecimal={false}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.book_value}
                    label={t(`${Tables.E2_DYNAMIC_DATA}.columns.book_value`)}
                    control={form.control}
                  />
                </div>

                <Divider />
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? NewDiff(changedFields, [], {}, Tables.E2_DYNAMIC_DATA)
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
